/**
 * GoogleCalendarConnect - Component for providers to connect their Google Calendar
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Calendar, Link, Unlink, RefreshCw, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GoogleCalendarConnect({ providerId, onStatusChange }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [disconnectOpen, setDisconnectOpen] = useState(false);

  useEffect(() => {
    fetchStatus();
    
    // Check for callback params
    const params = new URLSearchParams(window.location.search);
    if (params.get('calendar_connected') === 'true') {
      toast.success(`Google Calendar connected! Email: ${params.get('email')}`);
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      fetchStatus();
    } else if (params.get('calendar_error')) {
      toast.error('Failed to connect Google Calendar. Please try again.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [providerId]);

  const fetchStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/provider/${providerId}/status`);
      if (response.ok) {
        const data = await response.json();
        setStatus(data);
        onStatusChange?.(data);
      }
    } catch (error) {
      console.error('Failed to fetch calendar status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/google/auth-url?provider_id=${providerId}`);
      if (response.ok) {
        const data = await response.json();
        // Redirect to Google OAuth
        window.location.href = data.authorization_url;
      } else {
        toast.error('Failed to get authorization URL');
      }
    } catch (error) {
      toast.error('Connection failed');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const response = await fetch(`${API_URL}/api/calendar/provider/${providerId}/sync`, {
        method: 'POST'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Synced ${data.busy_dates_imported} busy dates from calendar`);
        fetchStatus();
      } else {
        toast.error('Sync failed');
      }
    } catch (error) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      const response = await fetch(`${API_URL}/api/calendar/provider/${providerId}/disconnect`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Google Calendar disconnected');
        setStatus({ connected: false });
        setDisconnectOpen(false);
        onStatusChange?.({ connected: false });
      } else {
        toast.error('Failed to disconnect');
      }
    } catch (error) {
      toast.error('Disconnection failed');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Google Calendar</CardTitle>
                <CardDescription>Sync your availability with Google Calendar</CardDescription>
              </div>
            </div>
            {status?.connected ? (
              <Badge className="bg-green-100 text-green-700">Connected</Badge>
            ) : (
              <Badge variant="secondary">Not Connected</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {status?.connected ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span>Connected as <strong>{status.google_email}</strong></span>
              </div>
              
              {status.last_sync && (
                <p className="text-xs text-gray-500">
                  Last synced: {new Date(status.last_sync).toLocaleString()}
                </p>
              )}
              
              <div className="flex gap-2">
                <Button 
                  onClick={handleSync} 
                  disabled={syncing}
                  className="flex-1"
                >
                  {syncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Sync Now
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setDisconnectOpen(true)}
                  className="text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              </div>
              
              <p className="text-xs text-gray-500">
                Syncing imports busy times from your Google Calendar to block those dates automatically.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Connect your Google Calendar to automatically sync your availability 
                and prevent double-bookings.
              </p>
              
              <ul className="text-sm text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Auto-block dates when you're busy
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Add bookings to your calendar
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Prevent double-bookings
                </li>
              </ul>
              
              <Button onClick={handleConnect} className="w-full bg-blue-600 hover:bg-blue-700">
                <Link className="w-4 h-4 mr-2" />
                Connect Google Calendar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={disconnectOpen} onOpenChange={setDisconnectOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Google Calendar?</DialogTitle>
            <DialogDescription>
              This will stop syncing your availability with Google Calendar. 
              Existing blocked dates will remain, but new events won't be synced.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDisconnectOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDisconnect}>
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
